import React, { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { TSP_MAP_CONFIG } from "../../../constants/tspMapConfig";
import { outsideIndiaMask } from "../coverageMapModel";
import useTspStateBoundaries from "../useTspStateBoundaries";
import { HEALTH_UI, formatAccuracy, linesToGeoJson, timeAgo } from "./trackingModel";

// The Live Location map: each tracker's movement path (one line per tracking
// session, so a gap is never drawn as a jump), a "Start" marker, and their
// current / last known position with its accuracy circle. Same base map as the
// coverage map (constants/tspMapConfig.js), and like it India only: everything
// outside India's Survey of India boundary is covered by a plain mask (other
// countries and their names are not shown), and the map cannot be panned away.
//
//   lines           Map(session_id -> { userId, coords: [[lng, lat]] }) — appended to, never rebuilt
//   trackers        GET /tracking `trackers`
//   colorFor        (userId) => colour
//   selectedUserId  whose marker to follow
//   follow          keep the selected marker in view as it moves
//   onUserMove      the user dragged/zoomed the map (the caller stops following)
//   fitKey          a NEW value fits the map to everything once
const PATH = "tsp-live-path";
const ACCURACY = "tsp-live-accuracy";
const STREET_ZOOM = 16;
// India's extent plus a margin: the furthest the map can be panned.
const INDIA_MAX_BOUNDS = [
  [60.5, 2.5],
  [104.5, 40.5],
];

// Covers everything outside India (and draws India's border above the mask),
// under the tracking layers so routes and accuracy circles stay on top.
function addIndiaOnlyMask(map, national) {
  if (!national || map.getSource("tsp-india")) return;
  const mask = outsideIndiaMask(national);
  map.addSource("tsp-india", { type: "geojson", data: national });
  const before = map.getLayer(`${ACCURACY}-fill`) ? `${ACCURACY}-fill` : undefined;
  if (mask) {
    map.addSource("tsp-outside-india", { type: "geojson", data: mask });
    map.addLayer({ id: "tsp-outside-india-mask", type: "fill", source: "tsp-outside-india", paint: { "fill-color": TSP_MAP_CONFIG.outsideIndiaColor, "fill-opacity": 1 } }, before);
  }
  map.addLayer(
    { id: "tsp-india-border", type: "line", source: "tsp-india", paint: { "line-color": "#6b5b7b", "line-width": ["interpolate", ["linear"], ["zoom"], 3, 1.1, 8, 2.2], "line-opacity": 0.85 } },
    before
  );
}

// A circle of `radius` metres as a polygon (for the accuracy halo).
function circle([lng, lat], radius, steps = 48) {
  const coords = [];
  const dLat = radius / 111320;
  const dLng = radius / (111320 * Math.cos((lat * Math.PI) / 180) || 1);
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * 2 * Math.PI;
    coords.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
  }
  return coords;
}

function accuracyGeoJson(trackers, colorFor) {
  return {
    type: "FeatureCollection",
    features: trackers
      .filter((tracker) => tracker.last_fix && tracker.last_fix.accuracy > 0 && tracker.health === "LIVE")
      .map((tracker) => ({
        type: "Feature",
        properties: { color: colorFor(tracker.user.id) },
        geometry: { type: "Polygon", coordinates: [circle([tracker.last_fix.lng, tracker.last_fix.lat], Math.min(tracker.last_fix.accuracy, 5000))] },
      })),
  };
}

function initials(name) {
  return (name || "?")
    .replace(/^TSPTEST\s+/i, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function trackerTitle(tracker) {
  const health = HEALTH_UI[tracker.health] || HEALTH_UI.OFF;
  const accuracy = formatAccuracy(tracker.last_fix?.accuracy);
  return [tracker.user.name || "Unknown", health.label, tracker.last_fix ? `Updated ${timeAgo(tracker.last_fix.t)}` : null, accuracy ? `Accuracy ${accuracy}` : null].filter(Boolean).join(" · ");
}

export default function TspLiveLocationMap({ lines, trackers, colorFor, selectedUserId, follow, onUserMove, fitKey }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef(new Map()); // userId -> maplibregl.Marker (current position)
  const startMarkers = useRef(new Map()); // session_id -> Marker
  const handlers = useRef({});
  handlers.current = { onUserMove };
  const [ready, setReady] = useState(false);
  const [baseMapFailed, setBaseMapFailed] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const { national } = useTspStateBoundaries();

  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: TSP_MAP_CONFIG.styleUrl,
      center: TSP_MAP_CONFIG.india.center,
      zoom: TSP_MAP_CONFIG.india.zoom,
      minZoom: 3,
      maxZoom: 19,
      maxBounds: INDIA_MAX_BOUNDS,
      renderWorldCopies: false, // one world only, so the mask covers everything outside India
      attributionControl: { compact: true, customAttribution: TSP_MAP_CONFIG.statesAttribution },
    });
    const collapseAttribution = () => {
      const attrib = containerRef.current?.querySelector?.(".maplibregl-ctrl-attrib");
      if (attrib) {
        attrib.classList.remove("maplibregl-compact-show");
        attrib.classList.add("maplibregl-compact");
        attrib.removeAttribute("open");
      }
    };

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
    setTimeout(collapseAttribution, 0);

    const checkZoom = () => {
      if (!map || typeof map.getZoom !== "function") return;
      setIsZoomed(map.getZoom() > 4.5);
    };

    map.on("load", () => {
      TSP_MAP_CONFIG.hiddenStyleLayers.forEach((id) => map.getLayer(id) && map.setLayoutProperty(id, "visibility", "none"));
      collapseAttribution();
      map.addSource(ACCURACY, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: `${ACCURACY}-fill`, type: "fill", source: ACCURACY, paint: { "fill-color": ["get", "color"], "fill-opacity": 0.12 } });
      map.addSource(PATH, { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addLayer({ id: `${PATH}-casing`, type: "line", source: PATH, layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": "#ffffff", "line-width": 7, "line-opacity": 0.9 } });
      map.addLayer({ id: `${PATH}-line`, type: "line", source: PATH, layout: { "line-join": "round", "line-cap": "round" }, paint: { "line-color": ["get", "color"], "line-width": 4 } });
      setReady(true);
    });
    map.on("styledata", collapseAttribution);
    map.on("error", (event) => {
      if (!map.isStyleLoaded() && event && event.error && /style/i.test(String(event.error.message || ""))) setBaseMapFailed(true);
    });
    map.on("zoomend", checkZoom);
    map.on("moveend", checkZoom);
    // Only a real user gesture (it carries the DOM event) stops "follow".
    const userMoved = (event) => event && event.originalEvent && handlers.current.onUserMove && handlers.current.onUserMove();
    map.on("dragstart", userMoved);
    map.on("wheel", userMoved);
    map.on("touchstart", userMoved);
    mapRef.current = map;
    const currentMarkers = markers.current;
    const currentStarts = startMarkers.current;
    return () => {
      currentMarkers.forEach((marker) => marker.remove());
      currentMarkers.clear();
      currentStarts.forEach((marker) => marker.remove());
      currentStarts.clear();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const container = containerRef.current;
    if (!map || !container || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => map.resize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [ready]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && ready && national) addIndiaOnlyMask(map, national);
  }, [ready, national]);

  // Path lines — updated only when new points arrived (`lines` changes identity).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getSource(PATH)?.setData(linesToGeoJson(lines, colorFor));

    // One "Start" marker per session (added once, never moved).
    lines.forEach((line, sessionId) => {
      if (startMarkers.current.has(sessionId) || !line.coords.length) return;
      const el = document.createElement("div");
      el.className = "tsp-live-start";
      el.style.borderColor = colorFor(line.userId);
      el.title = "Tracking started here";
      el.setAttribute("aria-label", "Tracking start point");
      startMarkers.current.set(sessionId, new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(line.coords[0]).addTo(map));
    });
  }, [lines, ready, colorFor]);

  // Current-position markers: created once per user, then only moved.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    trackers.forEach((tracker) => {
      if (!tracker.last_fix) return;
      const userId = tracker.user.id;
      seen.add(userId);
      const lngLat = [tracker.last_fix.lng, tracker.last_fix.lat];
      let marker = markers.current.get(userId);
      if (!marker) {
        const el = document.createElement("div");
        el.innerHTML = '<span class="tsp-live-marker-pulse"></span><span class="tsp-live-marker-core"></span>';
        el.tabIndex = 0;
        marker = new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat(lngLat).addTo(map);
        markers.current.set(userId, marker);
      } else {
        marker.setLngLat(lngLat);
      }
      const el = marker.getElement();
      el.className = `tsp-live-marker health-${tracker.health.toLowerCase()}${String(userId) === String(selectedUserId) ? " is-selected" : ""}`;
      el.style.setProperty("--tsp-live-color", colorFor(userId));
      el.dataset.initials = initials(tracker.user.name);
      el.title = trackerTitle(tracker);
      el.setAttribute("aria-label", trackerTitle(tracker));
    });
    markers.current.forEach((marker, userId) => {
      if (!seen.has(userId)) {
        marker.remove();
        markers.current.delete(userId);
      }
    });
    if (ready) map.getSource(ACCURACY)?.setData(accuracyGeoJson(trackers, colorFor));
  }, [trackers, ready, selectedUserId, colorFor]);

  // Follow the selected tracker as it moves.
  const selected = trackers.find((tracker) => String(tracker.user.id) === String(selectedUserId));
  const followLat = selected?.last_fix?.lat;
  const followLng = selected?.last_fix?.lng;
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !follow || followLat === undefined) return;
    map.easeTo({ center: [followLng, followLat], zoom: Math.max(map.getZoom ? map.getZoom() : STREET_ZOOM, 14), duration: 800 });
  }, [follow, followLat, followLng]);

  // Fit everything (paths + positions) when asked.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !fitKey) return;
    // A loop, not Math.min(...all): a long journey has too many points to spread.
    const bounds = [
      [Infinity, Infinity],
      [-Infinity, -Infinity],
    ];
    const include = ([lng, lat]) => {
      bounds[0][0] = Math.min(bounds[0][0], lng);
      bounds[0][1] = Math.min(bounds[0][1], lat);
      bounds[1][0] = Math.max(bounds[1][0], lng);
      bounds[1][1] = Math.max(bounds[1][1], lat);
    };
    lines.forEach((line) => line.coords.forEach(include));
    trackers.forEach((tracker) => tracker.last_fix && include([tracker.last_fix.lng, tracker.last_fix.lat]));
    if (!Number.isFinite(bounds[0][0])) return;
    if (bounds[0][0] === bounds[1][0] && bounds[0][1] === bounds[1][1]) map.jumpTo({ center: bounds[0], zoom: STREET_ZOOM });
    else map.fitBounds(bounds, { padding: 56, maxZoom: STREET_ZOOM + 1, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a NEW fit request moves the map
  }, [fitKey]);

  const handleResetToFullMap = () => {
    const map = mapRef.current;
    if (!map) return;
    map.fitBounds(TSP_MAP_CONFIG.india.bounds, { padding: 12, duration: 600 });
    setIsZoomed(false);
  };

  return (
    <div className="tsp-map-wrap tsp-live-map-wrap">
      <div ref={containerRef} className="tsp-map tsp-live-map" role="region" aria-label="Live location map" data-testid="tsp-live-map" />
      {isZoomed && (
        <button
          type="button"
          className="tsp-map-back-btn"
          onClick={handleResetToFullMap}
          title="Back to full map"
          aria-label="Back to full map"
        >
          <RotateCcw size={13} aria-hidden="true" />
          <span>Back to Map</span>
        </button>
      )}
      {baseMapFailed && <div className="tsp-cov-map-empty">The base map could not be loaded. Positions and the route are still drawn at their coordinates.</div>}
    </div>
  );
}
